// Upload rights consent (2026-10-14).
//
// Before the first upload into any tool, the visitor ticks "I own or have
// permission to use everything I upload". One tick per tool, per account
// (or per browser when signed out), per wording VERSION — bump the version
// when the wording changes and everyone is asked again.
//
// How a tool opts in: put data-consent-tool="<tool>" on its
// <input type="file"> (or any ancestor). RightsConsentHost listens to every
// click on the page in the capture phase, stops the picker from opening
// until the box is ticked, then opens it again from the Confirm button's own
// click. Drops and pastes that don't go through an input call
// requireRightsConsent(tool) themselves.
//
// Each tick is also POSTed to /api/web/consent as the server-side record.
import { api } from "@/lib/api";

export const RIGHTS_CONSENT_VERSION = "2026-10-14";

export type ConsentTool =
  | "chopbox"
  | "facelock"
  | "corrido"
  | "director"
  | "ads"
  | "storybook";

const KEY_PREFIX = "fs_rights_consent";
const ANON_KEY = "fs_rights_consent_anon";

function accountKey(): string {
  const token = api.getToken();
  if (!token) return "anon";
  try {
    const part = token.split(".")[1];
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    const id = json?.id ?? json?.sub;
    return id ? String(id) : "anon";
  } catch {
    return "anon";
  }
}

function storageKey(tool: string): string {
  return `${KEY_PREFIX}:${RIGHTS_CONSENT_VERSION}:${tool}:${accountKey()}`;
}

function anonId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).slice(0, 64);
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
}

// Ticked in this page's lifetime even when storage is blocked.
const memory = new Set<string>();

export function hasRightsConsent(tool: string): boolean {
  const key = storageKey(tool);
  if (memory.has(key)) return true;
  try {
    return !!localStorage.getItem(key);
  } catch {
    return false;
  }
}

export function grantRightsConsent(tool: string): void {
  const key = storageKey(tool);
  memory.add(key);
  try {
    localStorage.setItem(key, new Date().toISOString());
  } catch {
    /* memory copy covers this page */
  }
  const base = (import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_PUBLIC_URL || "").replace(/\/$/, "");
  const token = api.getToken();
  void fetch(`${base}/api/web/consent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ tool, version: RIGHTS_CONSENT_VERSION, anon_id: anonId() }),
    keepalive: true,
  }).catch(() => undefined);
}

type Pending = { tool: string; resolve: (ok: boolean) => void; onConfirm?: () => void };
type Listener = (p: Pending | null) => void;

let pending: Pending | null = null;
const listeners = new Set<Listener>();

export function subscribeRightsConsent(fn: Listener): () => void {
  listeners.add(fn);
  fn(pending);
  return () => {
    listeners.delete(fn);
  };
}

function emit() {
  for (const fn of listeners) fn(pending);
}

/**
 * Resolves true at once when the tool already has consent, otherwise shows
 * the dialog and resolves with the visitor's answer. onConfirm runs inside
 * the Confirm click itself, for work that needs a user gesture (reopening a
 * file picker).
 */
export function requireRightsConsent(tool: string, onConfirm?: () => void): Promise<boolean> {
  if (hasRightsConsent(tool)) {
    onConfirm?.();
    return Promise.resolve(true);
  }
  // No host mounted (tests, a stray entry point): never block the tool.
  if (listeners.size === 0) {
    onConfirm?.();
    return Promise.resolve(true);
  }
  if (pending) pending.resolve(false);
  return new Promise<boolean>((resolve) => {
    pending = { tool, resolve, onConfirm };
    emit();
  });
}

export function settleRightsConsent(ok: boolean): void {
  const p = pending;
  if (!p) return;
  pending = null;
  if (ok) {
    grantRightsConsent(p.tool);
    p.onConfirm?.();
  }
  p.resolve(ok);
  emit();
}
